// src/pages/useContentProtection.js
//
// Content protection (ADR-009, confirmed launch requirement 2026-06-18): raise FRICTION
// against casual copying of analysis content -- NOT unbreakable DRM. Faithful port of the
// prototype content-protection useEffect (between-the-lines-v2.html L13959-14004).
//
// Mounted ONCE by ReaderPageShell, so BOTH readers (set-text + literature) inherit it via
// document-level listeners. Blocks, on `.analysis-content` only (and globally for the
// shortcut keys): right-click (contextmenu), copy + cut, and Ctrl/Cmd + A/C/X/U/S/P plus
// F12 / devtools shortcuts. EXEMPTS anything inside `.selectable` (the AI drawer output +
// any Notes input) so users can still copy their own notes and AI chat. Navigation,
// metadata and UI chrome are NOT inside `.analysis-content`, so they are never blocked.
//
// Returns `protectToast` (bool); the mounting shell renders the prototype's protect toast.
import { useState, useEffect, useRef } from 'react';

export function useContentProtection() {
    const [protectToast, setProtectToast] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        const showProtectToast = () => {
            setProtectToast(true);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setProtectToast(false), 1800);
        };
        const inAnalysis = (target) => target?.closest?.('.analysis-content');
        const inSelectable = (target) => target?.closest?.('.selectable');

        const onContextMenu = (e) => {
            if (inAnalysis(e.target) && !inSelectable(e.target)) {
                e.preventDefault();
                showProtectToast();
            }
        };
        const onKeyDown = (e) => {
            const key = (e.key || '').toLowerCase();
            // Always block devtools shortcuts while a reader is open.
            if (key === 'f12' || (e.ctrlKey && e.shiftKey && key === 'i') || (e.metaKey && e.altKey && key === 'i')) {
                e.preventDefault(); showProtectToast(); return;
            }
            // PrintScreen -- can't truly block, but inform the user.
            if (key === 'printscreen') {
                showProtectToast(); return;
            }
            const mod = e.ctrlKey || e.metaKey;
            if (!mod) return;
            if (!['c', 'a', 'x', 'u', 's', 'p'].includes(key)) return;
            // Allow inside Notes / AI chat (anything marked .selectable).
            if (inSelectable(e.target)) return;
            e.preventDefault();
            showProtectToast();
        };
        const onCopy = (e) => {
            if (inAnalysis(e.target) && !inSelectable(e.target)) {
                e.preventDefault();
                showProtectToast();
            }
        };

        document.addEventListener('contextmenu', onContextMenu);
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('copy', onCopy);
        document.addEventListener('cut', onCopy);
        return () => {
            document.removeEventListener('contextmenu', onContextMenu);
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('copy', onCopy);
            document.removeEventListener('cut', onCopy);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    return protectToast;
}
